import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DataTable } from '@components/DataTable';
import { DataTableColumn } from '@components/DataTable/types';
import { useListScoringBatches } from '../hooks/useListScoringBatches';
import { ScoringBatchSummary, ScoringMode } from '../types';

const MODE_LABELS: Record<ScoringMode, string> = {
    many_to_one: 'Talents sélectionnés → 1 Offre',
    one_to_many: '1 Talent → Plusieurs offres',
    all_to_one: 'Talents → 1 Offre',
    one_to_all: '1 Talent → Offres',
    one_to_one: '1 Talent → 1 Offre',
};

const formatBatchDate = (value?: string | number | Date) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('fr-FR');
};

const StatusBar = ({ row }: { row: ScoringBatchSummary }) => {
    const theme = useTheme();
    if (row.total === 0) return <Typography variant="caption">—</Typography>;
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {row.done > 0 && (
                <Chip
                    label={`${row.done} ok`}
                    size="small"
                    sx={{ bgcolor: theme.palette.successGreen?.main ?? 'success.main', color: '#fff', fontSize: 11 }}
                />
            )}
            {row.pending > 0 && (
                <Chip
                    label={`${row.pending} en attente`}
                    size="small"
                    sx={{ bgcolor: theme.palette.warning.main, color: '#fff', fontSize: 11 }}
                />
            )}
            {row.inProgress > 0 && (
                <Chip
                    label={`${row.inProgress} en cours`}
                    size="small"
                    sx={{ bgcolor: theme.palette.info.main, color: '#fff', fontSize: 11 }}
                />
            )}
            {row.error > 0 && (
                <Chip
                    label={`${row.error} erreur`}
                    size="small"
                    sx={{ bgcolor: theme.palette.error.main, color: '#fff', fontSize: 11 }}
                />
            )}
        </Box>
    );
};

export const ScoringBatchesTable = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const { docs, totalDocs, loading } = useListScoringBatches(page, pageSize);

    const columns = useMemo<DataTableColumn<ScoringBatchSummary>[]>(
        () => [
            {
                id: 'batchId',
                label: 'Batch ID',
                field: 'batchId',
                minWidth: '160px',
                renderCell: ({ row }) => (
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            {row.batchId}
                        </Typography>
                    </Box>
                ),
            },
            {
                id: 'mode',
                label: 'Mode',
                field: 'mode',
                minWidth: '140px',
                renderCell: ({ row }) => (
                    <Chip label={MODE_LABELS[row.mode]} size="small" sx={{ fontSize: 11 }} />
                ),
            },
            {
                id: 'total',
                label: 'Total',
                field: 'total',
                minWidth: '70px',
            },
            {
                id: 'status',
                label: 'Statut',
                minWidth: '260px',
                renderCell: ({ row }) => <StatusBar row={row} />,
            },
            {
                id: 'createdAt',
                label: 'Déclenché le',
                field: 'createdAt',
                minWidth: '160px',
                renderCell: ({ row }) => {
                    const formatted = formatBatchDate(row.createdAt as string | number | Date | undefined);
                    return formatted ? (
                        <Typography variant="body2">{formatted}</Typography>
                    ) : (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            —
                        </Typography>
                    );
                },
            },
            {
                id: 'actions',
                label: '',
                minWidth: '60px',
                renderCell: ({ row }) => (
                    <Tooltip title="Voir le batch">
                        <IconButton
                            size="small"
                            onClick={() => navigate(`/scoring/batch/${row.batchId}`)}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            },
        ],
        [navigate, theme],
    );

    return (
        <DataTable
            rows={docs}
            columns={columns}
            loading={loading}
            height="400px"
            stickyHeader
            pagination={{
                currentPage: page,
                pageSize,
                totalRows: totalDocs,
                onPageChange: setPage,
                onPageSizeChange: (newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(1);
                },
            }}
        />
    );
};

export default ScoringBatchesTable;
